/* __next_internal_client_entry_do_not_use__ ROUNDING_THRESHOLD,BaseFiniteScrollCarousel auto */ import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { ScrollWrapper } from "@faire/retailer-visitor-shared/components/Scroll/ScrollWrapper";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { trackEvent } from "@faire/web--source/common/trackEvent";
import { IconButton, StyledIconWrapper } from "@faire/web--source/slate/IconButton/IconButton";
import { Chevron } from "@faire/web--source/slate/icons/Chevron";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { observer } from "mobx-react";
import * as React from "react";
import { useState } from "react";
import styled from "styled-components";
var BUTTON_WIDTH = 35;
export var ROUNDING_THRESHOLD = 1;
var Direction;
(function(Direction) {
    Direction["NEXT"] = "next";
    Direction["PREVIOUS"] = "previous";
})(Direction || (Direction = {}));
var BaseFiniteScrollCarouselBase = function(param) {
    var chevronButtonDimension = param.chevronButtonDimension, children = param.children, className = param.className, enableDrag = param.enableDrag, forwardRef = param.forwardRef, name = param.name, onLeftEndReached = param.onLeftEndReached, onRightEndReached = param.onRightEndReached, onVisible = param.onVisible, _param_peekGapPercentage = param.peekGapPercentage, peekGapPercentage = _param_peekGapPercentage === void 0 ? 25 : _param_peekGapPercentage, _param_peekGapPixel = param.peekGapPixel, peekGapPixel = _param_peekGapPixel === void 0 ? 0 : _param_peekGapPixel, scrollConfiguration = param.scrollConfiguration, shouldHideLeftButton = param.shouldHideLeftButton, shouldHideRightButton = param.shouldHideRightButton, useIconArrows = param.useIconArrows, iconArrowsOffsetTop = param.iconArrowsOffsetTop;
    var _useState = _sliced_to_array(useState(false), 2), isMouseDown = _useState[0], setIsMouseDown = _useState[1];
    var _useState1 = _sliced_to_array(useState(false), 2), isRightEndReached = _useState1[0], setIsRightEndReached = _useState1[1];
    var _useState2 = _sliced_to_array(useState(true), 2), isLeftEndReached = _useState2[0], setIsLeftEndReached = _useState2[1];
    var _useState3 = _sliced_to_array(useState(), 2), mouseDownRelativeXCoordinate = _useState3[0], setMouseDownRelativeXCoordinate = _useState3[1];
    var _useState4 = _sliced_to_array(useState(), 2), scrollLeft = _useState4[0], setScrollLeft = _useState4[1];
    var strictLocalize = useStrictLocalization().strictLocalize;
    var scrollRef = forwardRef;
    var handleMouseDown = function(isDown) {
        if (!enableDrag) {
            return;
        }
        setIsMouseDown(isDown);
    };
    var onMouseDown = function(event) {
        if (!enableDrag) {
            return;
        }
        var currentElement = scrollRef.current;
        if (!currentElement) {
            return;
        }
        handleMouseDown(true);
        var x = currentElement.getBoundingClientRect().x;
        // record starting x coordinate relative to carousel
        setMouseDownRelativeXCoordinate(event.pageX - x);
        setScrollLeft(currentElement.scrollLeft);
    };
    var onMouseMove = function(event) {
        var // scroll carousel based on the distance mouse moved
        _currentElement_scrollTo;
        if (!enableDrag) {
            return;
        }
        if (!isMouseDown) {
            return;
        }
        var currentElement = scrollRef.current;
        if (!currentElement || mouseDownRelativeXCoordinate === undefined || scrollLeft === undefined) {
            return;
        }
        event.preventDefault();
        var x = currentElement.getBoundingClientRect().x;
        // calculate mouse x coordinate relative to the carousel
        var mouseRelativeXCoordinate = event.pageX - x;
        // calculate distance mouse moved
        var movingDistance = mouseRelativeXCoordinate - mouseDownRelativeXCoordinate;
        (_currentElement_scrollTo = currentElement.scrollTo) === null || _currentElement_scrollTo === void 0 ? void 0 : _currentElement_scrollTo.call(currentElement, {
            left: scrollLeft - movingDistance
        });
    };
    var onScroll = function() {
        var element = scrollRef.current;
        if (!element) {
            return;
        }
        var width = element.getBoundingClientRect().width;
        // since scrollLeft and scrollWidth are calculated as integers we subtract ROUNDING_THRESHOLD to account for errors
        var isRightEndReachedAfterScroll = element.scrollLeft + Math.ceil(width) >= element.scrollWidth - ROUNDING_THRESHOLD;
        if (isRightEndReachedAfterScroll && !isRightEndReached) {
            trackEvent("/".concat(name, "/finite-scroll-carousel/last-item"));
        }
        setIsRightEndReached(isRightEndReachedAfterScroll);
        setIsLeftEndReached(!(element === null || element === void 0 ? void 0 : element.scrollLeft));
        onRightEndReached === null || onRightEndReached === void 0 ? void 0 : onRightEndReached(isRightEndReachedAfterScroll);
        onLeftEndReached === null || onLeftEndReached === void 0 ? void 0 : onLeftEndReached(!(element === null || element === void 0 ? void 0 : element.scrollLeft));
    };
    var renderButton = function() {
        var buttonHeight = chevronButtonDimension === null || chevronButtonDimension === void 0 ? void 0 : chevronButtonDimension.height.toString();
        var buttonWidth = chevronButtonDimension === null || chevronButtonDimension === void 0 ? void 0 : chevronButtonDimension.width.toString();
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                shouldHideLeftButton ? null : /*#__PURE__*/ _jsx(PreviousButton, {
                    onClick: onClick(Direction.PREVIOUS),
                    "data-test-id": "finite-scroll-btn-previous",
                    "aria-label": strictLocalize({
                        id: "dmlNx/",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Previous Carousel Slide"
                            }
                        ]
                    }),
                    disabled: isLeftEndReached,
                    $isDeadEndReached: isLeftEndReached,
                    children: /*#__PURE__*/ _jsx(Chevron, {
                        direction: "left",
                        color: "inherit",
                        height: buttonHeight,
                        width: buttonWidth
                    })
                }),
                shouldHideRightButton ? null : /*#__PURE__*/ _jsx(NextButton, {
                    onClick: onClick(Direction.NEXT),
                    "data-test-id": "finite-scroll-btn-next",
                    "aria-label": strictLocalize({
                        id: "kHyFNJ",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Next Carousel Slide"
                            }
                        ]
                    }),
                    disabled: isRightEndReached,
                    $isDeadEndReached: isRightEndReached,
                    children: /*#__PURE__*/ _jsx(Chevron, {
                        direction: "right",
                        color: "inherit",
                        height: buttonHeight,
                        width: buttonWidth
                    })
                })
            ]
        });
    };
    var renderIconButtons = function() {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                shouldHideLeftButton ? null : /*#__PURE__*/ _jsx(PreviousIconButton, {
                    onClick: onClick(Direction.PREVIOUS),
                    "data-test-id": "finite-scroll-btn-previous",
                    "aria-label": strictLocalize({
                        id: "dmlNx/",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Previous Carousel Slide"
                            }
                        ]
                    }),
                    variant: "tertiary",
                    size: "small",
                    disabled: isLeftEndReached,
                    offsetTop: iconArrowsOffsetTop,
                    children: /*#__PURE__*/ _jsx(Chevron, {
                        direction: "left"
                    })
                }),
                shouldHideRightButton ? null : /*#__PURE__*/ _jsx(NextIconButton, {
                    onClick: onClick(Direction.NEXT),
                    "data-test-id": "finite-scroll-btn-next",
                    "aria-label": strictLocalize({
                        id: "kHyFNJ",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Next Carousel Slide"
                            }
                        ]
                    }),
                    variant: "tertiary",
                    size: "small",
                    disabled: isRightEndReached,
                    offsetTop: iconArrowsOffsetTop,
                    children: /*#__PURE__*/ _jsx(Chevron, {
                        direction: "right"
                    })
                })
            ]
        });
    };
    var onClick = function(direction) {
        return function() {
            var _element_scrollTo;
            var element = scrollRef.current;
            if (!element) {
                return;
            }
            var width = element.getBoundingClientRect().width;
            var left = element.scrollLeft;
            var peekGapPercentageToPixel = peekGapPercentage * width / 100;
            var movePixel = scrollConfiguration ? scrollConfiguration.numTilesToMove * (scrollConfiguration.tileWidth + scrollConfiguration.gapWidth) : width - peekGapPixel - peekGapPercentageToPixel;
            if (direction === Direction.NEXT) {
                left += movePixel;
            } else if (direction === Direction.PREVIOUS) {
                left -= movePixel;
            }
            trackEvent("/".concat(name, "/finite-scroll-carousel/").concat(direction));
            (_element_scrollTo = element.scrollTo) === null || _element_scrollTo === void 0 ? void 0 : _element_scrollTo.call(element, {
                left: left,
                behavior: "smooth"
            });
        };
    };
    return /*#__PURE__*/ _jsxs(Wrapper, {
        className: className,
        ref: onVisible,
        children: [
            /*#__PURE__*/ _jsx(StyledScrollWrapper, {
                $horizontal: true,
                onMouseDown: onMouseDown,
                onMouseLeave: function() {
                    return handleMouseDown(false);
                },
                onMouseUp: function() {
                    return handleMouseDown(false);
                },
                onMouseMove: onMouseMove,
                onScroll: onScroll,
                ref: scrollRef,
                "data-test-id": "finite-scroll-wrapper",
                children: children
            }),
            useIconArrows ? renderIconButtons() : renderButton()
        ]
    });
};
var Wrapper = styled.div.withConfig({
    componentId: "sc-b86f80a5-0"
})([
    "width:100%;position:relative;"
]);
/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/IconButton/IconButton#IconButton"
 */ var Button = styled.button.withConfig({
    componentId: "sc-b86f80a5-1"
})([
    "position:absolute;top:calc(50% - ",
    "px);align-items:center;justify-content:center;cursor:",
    ";",
    "{display:none;}"
], BUTTON_WIDTH / 2, function(param) {
    var $isDeadEndReached = param.$isDeadEndReached;
    return $isDeadEndReached ? "default" : "pointer";
}, responsiveMediaQueries.mobile);
var PreviousButton = styled(Button).withConfig({
    componentId: "sc-b86f80a5-2"
})([
    "left:-",
    "px;"
], BUTTON_WIDTH / 2);
var NextButton = styled(Button).withConfig({
    componentId: "sc-b86f80a5-3"
})([
    "right:-",
    "px;"
], BUTTON_WIDTH / 2);
var StyledIconButton = styled(IconButton).withConfig({
    componentId: "sc-b86f80a5-4"
})([
    "position:absolute;top:",
    ";",
    "{svg{stroke:",
    ";}&:hover{svg{stroke:",
    ";}}}"
], function(props) {
    var _props_offsetTop;
    return (_props_offsetTop = props.offsetTop) !== null && _props_offsetTop !== void 0 ? _props_offsetTop : "50%";
}, StyledIconWrapper, function(props) {
    return props.arrowColor;
}, Core.icon.default);
var PreviousIconButton = styled(StyledIconButton).withConfig({
    componentId: "sc-b86f80a5-5"
})([
    "left:0;transform:translate(calc((100% + 8px) * -1),-50%);"
]);
var NextIconButton = styled(StyledIconButton).withConfig({
    componentId: "sc-b86f80a5-6"
})([
    "right:0;transform:translate(calc(100% + 8px),-50%);"
]);
var StyledScrollWrapper = styled(ScrollWrapper).withConfig({
    componentId: "sc-b86f80a5-7"
})([
    "overflow-y:hidden;&&&{display:flex;}scroll-snap-type:x mandatory;-ms-overflow-style:none;scrollbar-width:none;&::-webkit-scrollbar{display:none;}"
]);
export var BaseFiniteScrollCarousel;
(function(BaseFiniteScrollCarousel_1) {
    BaseFiniteScrollCarousel_1.BaseFiniteScrollCarousel = observer(BaseFiniteScrollCarouselBase);
    BaseFiniteScrollCarousel_1.Next = NextButton;
    BaseFiniteScrollCarousel_1.NextIcon = NextIconButton;
    BaseFiniteScrollCarousel_1.Previous = PreviousButton;
    BaseFiniteScrollCarousel_1.PreviousIcon = PreviousIconButton;
    BaseFiniteScrollCarousel_1.Container = Wrapper;
    BaseFiniteScrollCarousel_1.Scrollable = StyledScrollWrapper;
})(BaseFiniteScrollCarousel || (BaseFiniteScrollCarousel = {}));
